import React from 'react';
import PropTypes from 'prop-types';

const { bool, node, object, func } = PropTypes;

/** ZIndex class component to manage  ui stacking context */
export class ZIndexBase extends React.PureComponent {
  static propTypes = {
    /**
     * Flag to indicate if or not to assign z-index.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    assignZIndex: bool,
    /**
     * Children node may be actual components or portal that the ZIndex component
     * would wrap.
     */
    children: node.isRequired,
    /**
     * Flag to indicate if or not we need to disabled zIndex or if component wants to
     * handle zIndex by itself.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disableZIndex: bool,
    /**
     * Style  object to cascade
     */
    // eslint-disable-next-line react/forbid-prop-types
    style: object,
    /**
     * Method to increment topZIndex for the component. ZIndex would call this method
     * to update when a component needs to be mounted and needed higher zIndex.
     */
    incrementBaseZIndex: func,
    /**
     * Method to decrement topZIndex for the component. ZIndex would call this method
     * to update when a component is unmounted and we need to reset the zIndex applied.
     */
    decrementBaseZIndex: func,
    /**
     * Getter method to get topZIndex for the component from the Context.
     */
    getCurrentBaseZIndex: func
  };

  static defaultProps = {
    assignZIndex: false,
    disableZIndex: false,
    style: {},
    incrementBaseZIndex: null,
    decrementBaseZIndex: null,
    getCurrentBaseZIndex: null
  };

  constructor(props) {
    super(props);
    this.childZIndex = null;
    this.backdropLowered = false;
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.assignZIndex !== prevProps.assignZIndex &&
      !this.props.assignZIndex &&
      !this.props.disableZIndex
    ) {
      this.props.decrementBaseZIndex();
    }
  }

  /**
   * Method to copy and cascade css styles and classes to the child.
   *
   * @param pCurrentBaseZIndex - zIndex to be applied on child style.
   */
  copyAndCascadeStylesForChildren(pCurrentBaseZIndex) {
    let children;
    // eslint-disable-next-line no-negated-condition
    if (!this.props.disableZIndex) {
      if (this.props.style) {
        children = React.Children.map(this.props.children, child =>
          React.cloneElement(child, {
            style: {
              ...child.props.style,
              ...this.props.style,
              zIndex: pCurrentBaseZIndex
            }
          })
        );
      } else {
        children = React.Children.map(this.props.children, child =>
          React.cloneElement(child, {
            style: {
              ...child.props.style,
              zIndex: pCurrentBaseZIndex
            }
          })
        );
      }
    } else {
      children = React.cloneElement(this.props.children);
    }

    return children;
  }

  /**
   * Sub-render method to render children, when we have to update zIndex for components with portal.
   *
   * @param getCurrentBaseZIndex - zIndex method to be used for applying on the style.
   */
  renderChildrenWithZIndexWhenPortalShown(getCurrentBaseZIndex) {
    const { disableZIndex, incrementBaseZIndex } = this.props;
    if (!this.childZIndex) {
      if (!disableZIndex) {
        incrementBaseZIndex();
        this.childZIndex = getCurrentBaseZIndex();
      }
    }

    return this.copyAndCascadeStylesForChildren(this.childZIndex);
  }

  /**
   * Sub-render method to render children, when we have to update zIndex for components with no portal.
   */
  renderChildrenWithZIndexWhenPortalNotShown() {
    this.childZIndex = null;
    return this.copyAndCascadeStylesForChildren(this.childZIndex);
  }

  render() {
    const { assignZIndex, getCurrentBaseZIndex } = this.props;
    return assignZIndex
      ? this.renderChildrenWithZIndexWhenPortalShown(getCurrentBaseZIndex)
      : this.renderChildrenWithZIndexWhenPortalNotShown();
  }
}

ZIndexBase.displayName = 'ZIndex';
export default ZIndexBase;
